import { gql } from "@apollo/client";
import { toItemInput } from "@shared/graphql.conversions";
import {
  BASKET_PAGE_FRAGMENT,
  BASKET_UPDATE_FRAGMENT
} from "@shared/graphql.fragments";
import { createRequestContext, getClient } from "lib/apolloClient";
import { setBasket } from "lib/basket";
import { dispatch } from "@shared/utils.events";
import { setNotifications } from "@shared/utils.notifications";
import { setTermsAndConditions } from "lib/termsAndConditions";
import { setUser } from "lib/user";
import type { BasketUpdateResultType, ItemType } from "@shared/graphql.types";
import { removeNull } from "utils/removeNull";
import { withGlobalSpinner } from "./api";
import { getTrackingEventId } from "./utils/trackingEventId";

const REMOVE_BASKET_ITEMS = gql`
  mutation removeBasketItems(
    $listId: ID!
    $version: Int!
    $items: [ItemInput!]!
  ) {
    removeBasketItems(listId: $listId, version: $version, items: $items) {
      ...BasketUpdateFragment
    }
  }
  ${BASKET_UPDATE_FRAGMENT}
`;

const REMOVE_BASKET_ITEMS_FROM_BASKET_PAGE = gql`
  mutation removeBasketItemsFromBasketPage(
    $listId: ID!
    $version: Int!
    $items: [ItemInput!]!
  ) {
    removeBasketItems(listId: $listId, version: $version, items: $items) {
      ...BasketPageFragment
    }
  }
  ${BASKET_PAGE_FRAGMENT}
`;

async function removeBasketItemsInner(
  items: ItemType[] = [],
  locationType?: string
) {
  const variables = {
    listId: "",
    version: -1,
    items: items.map(toItemInput)
  };
  const eventId = getTrackingEventId();

  const isBasketPage = window.location.pathname.includes("/basket");

  const result = await getClient().mutate<{
    removeBasketItems: BasketUpdateResultType;
  }>({
    mutation: isBasketPage
      ? REMOVE_BASKET_ITEMS_FROM_BASKET_PAGE
      : REMOVE_BASKET_ITEMS,
    variables,
    context: createRequestContext({ sessionStrategy: "START_NEW_ANONYMOUS" })
  });
  if (result?.data?.removeBasketItems) {
    const {
      items: basket,
      discounts,
      pricing,
      includedDiscounts,
      rejectedDiscounts,
      notifications,
      hasOnlyVirtualProducts,
      deliveryMethod,
      deliveryMethodName,
      termsAndConditions,
      gwpModule,
      thresholds,
      spotlightModule
    } = result.data.removeBasketItems;

    setUser({
      basket,
      discounts,
      pricing,
      includedDiscounts,
      rejectedDiscounts,
      hasOnlyVirtualProducts: !!hasOnlyVirtualProducts,
      deliveryMethod,
      deliveryMethodName: removeNull(deliveryMethodName),
      spotlightModule
    });

    setBasket({ gwpModule, thresholds, items: basket });

    setTermsAndConditions(termsAndConditions);

    setNotifications(notifications);

    dispatch("removeFromBag", {
      items: variables.items,
      event_id: eventId,
      locationType
    });
  }

  if (!result.data) throw new Error("No response received");

  return result.data.removeBasketItems;
}

export const removeBasketItems = (
  items: ItemType[] = [],
  locationType?: string,
  options?: { runInBackground?: boolean }
): Promise<BasketUpdateResultType> => {
  if (options?.runInBackground) {
    return removeBasketItemsInner(items, locationType);
  }

  return withGlobalSpinner(() => removeBasketItemsInner(items, locationType))();
};
